/**
 * Wishlist Module for Shopify Dawn Theme
 * Persists wishlist via localStorage.
 * Dispatches 'wishlist:updated' custom event on every change.
 * Exposes window.Wishlist for external access.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'shopify_wishlist';

  var CART_ICON_SVG = '<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M15.75 11.8h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33L28.4 11.8zm0 1h-2.22l-.71 10.67a4 4 0 0 0 3.99 4.27h7.38a4 4 0 0 0 4-4.27l-.72-10.67h-2.22v.63a4.75 4.75 0 1 1-9.5 0zm8.5 0h-7.5v.63a3.75 3.75 0 1 0 7.5 0z"/></svg>';

  var REMOVE_ICON_SVG = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"/><path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"/></svg>';

  // ─── Core Wishlist API ──────────────────────────────────────────────────────

  var Wishlist = {
    /** @returns {string[]} Array of product handles */
    get: function () {
      try {
        var data = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return Array.isArray(data) ? data : [];
      } catch (e) {
        return [];
      }
    },

    /** @param {string[]} items */
    save: function (items) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...new Set(items)]));
    },

    /** @param {string} handle */
    add: function (handle) {
      var items = this.get();
      if (!items.includes(handle)) {
        items.push(handle);
        this.save(items);
      }
      this._dispatch(handle);
    },

    /** @param {string} handle */
    remove: function (handle) {
      this.save(this.get().filter(function (h) { return h !== handle; }));
      this._dispatch(handle);
    },

    /** @param {string} handle */
    toggle: function (handle) {
      this.has(handle) ? this.remove(handle) : this.add(handle);
    },

    /** @param {string} handle @returns {boolean} */
    has: function (handle) {
      return this.get().includes(handle);
    },

    /** @returns {number} */
    count: function () {
      return this.get().length;
    },

    /** @param {string} handle */
    _dispatch: function (handle) {
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          bubbles: true,
          detail: { handle: handle, items: this.get() }
        })
      );
    }
  };

  window.Wishlist = Wishlist;

  // ─── UI: Sync all wishlist buttons ─────────────────────────────────────────

  function updateAllButtons() {
    document.querySelectorAll('[data-wishlist-handle]').forEach(function (btn) {
      var handle = btn.dataset.wishlistHandle;
      var isActive = Wishlist.has(handle);
      btn.classList.toggle('wishlist-btn--active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      var addLabel = btn.dataset.addLabel || 'Add to wishlist';
      var removeLabel = btn.dataset.removeLabel || 'Remove from wishlist';
      btn.setAttribute('aria-label', isActive ? removeLabel : addLabel);
    });
  }

  // ─── UI: Sync header count badge ───────────────────────────────────────────

  function updateHeaderCount() {
    var count = Wishlist.count();
    document.querySelectorAll('.wishlist-count-bubble').forEach(function (el) {
      if (count > 0) {
        el.removeAttribute('hidden');
        var numEl = el.querySelector('[data-wishlist-count]');
        if (numEl) numEl.textContent = count < 100 ? count : '99+';
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  // ─── Event: Handle wishlist button clicks ──────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-handle]');
    if (!btn) return;
    // Prevent parent <a> from navigating
    e.preventDefault();
    e.stopPropagation();
    Wishlist.toggle(btn.dataset.wishlistHandle);
  });

  // ─── Event: Sync all UI on wishlist change ─────────────────────────────────

  document.addEventListener('wishlist:updated', function () {
    updateAllButtons();
    updateHeaderCount();
    if (window.WishlistDrawer && typeof window.WishlistDrawer.render === 'function') {
      window.WishlistDrawer.render();
    }
  });

  // ─── Wishlist Page Rendering ────────────────────────────────────────────────

  /**
   * Format price in cents to store currency string.
   * Uses Shopify.formatMoney if available, falls back to wishlistConfig.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    var moneyFormat = (window.wishlistConfig && window.wishlistConfig.moneyFormat) || '{{amount}}';
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    // Simple fallback: replace {{amount}} with formatted number
    var amount = (cents / 100).toFixed(2);
    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  /** @param {string} str @returns {string} */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Build the price HTML for a wishlist card using Dawn's native .price classes.
   * @param {number} price
   * @param {number|null} compareAtPrice
   * @returns {string}
   */
  function buildPriceHtml(price, compareAtPrice) {
    if (compareAtPrice && compareAtPrice > price) {
      return (
        '<div class="price price--on-sale">' +
          '<div class="price__container">' +
            '<div class="price__regular">' +
              '<span class="visually-hidden visually-hidden--inline">Regular price</span>' +
              '<span class="price-item price-item--regular">' + formatMoney(compareAtPrice) + '</span>' +
            '</div>' +
            '<div class="price__sale">' +
              '<span class="visually-hidden visually-hidden--inline">Sale price</span>' +
              '<span class="price-item price-item--sale price-item--last">' + formatMoney(price) + '</span>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
    }
    return (
      '<div class="price">' +
        '<div class="price__container">' +
          '<div class="price__regular">' +
            '<span class="visually-hidden visually-hidden--inline">Regular price</span>' +
            '<span class="price-item price-item--regular">' + formatMoney(price) + '</span>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /**
   * Refresh Shopify cart count bubbles after add-to-cart.
   */
  function refreshCartCount() {
    return fetch('/?sections=cart-icon-bubble')
      .then(function (response) { return response.json(); })
      .then(function (sections) {
        var cartIcon = document.querySelector('#cart-icon-bubble');
        if (cartIcon && sections['cart-icon-bubble']) {
          cartIcon.innerHTML = sections['cart-icon-bubble'];
          return;
        }

        return fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
              el.textContent = cart.item_count;
            });
          });
      })
      .catch(function () {});
  }

  /**
   * Add item(s) to cart and keep the cart drawer / icon bubble in sync.
   * Mirrors the sections-based refresh product-form.js uses, since a plain
   * /cart/add.js call has no way to tell the cart drawer to re-render.
   * @param {Object} payload {id, quantity} for a single item, or {items: [...]}
   * @returns {Promise<boolean>} resolves true on success
   */
  function addToCart(payload) {
    var cartDrawer = document.querySelector('cart-drawer');
    var body = Object.assign({}, payload);

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer.getSectionsToRender().map(function (section) { return section.id; });
      body.sections_url = window.location.pathname;
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) return false;
      return response.json().then(function (data) {
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          closeWishlistDrawer();
          cartDrawer.renderContents(data);
        } else {
          refreshCartCount();
        }
        return true;
      });
    });
  }

  /**
   * Create a wishlist product card DOM element.
   * @param {Object} product Shopify AJAX product object
   * @returns {HTMLElement}
   */
  function createWishlistCard(product) {
    var cfg = window.wishlistConfig || {};
    var addToCartLabel = cfg.addToCartLabel || 'Add to Cart';
    var soldOutLabel = cfg.soldOutLabel || 'Sold Out';
    var addingLabel = cfg.addingLabel || 'Adding...';
    var addedLabel = cfg.addedLabel || 'Added!';

    // Find first available variant
    var variant = null;
    if (product.variants && product.variants.length) {
      variant = product.variants.find(function (v) { return v.available; }) || product.variants[0];
    }
    var available = variant && variant.available;
    var price = variant ? variant.price : product.price;
    var compareAtPrice = variant ? variant.compare_at_price : product.compare_at_price;

    // Build image URL – AJAX API returns full URL
    var imageUrl = product.featured_image
      ? product.featured_image.replace(/(\.\w+)$/, '_600x600$1')
      : null;

    // Build wishlist heart button (always active/filled on the wishlist page)
    var heartPath = 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';
    var svgAttrs = 'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';

    var wrapper = document.createElement('div');
    wrapper.className = 'card-wrapper product-card-wrapper underline-links-hover';
    wrapper.setAttribute('data-wishlist-card-handle', product.handle);

    wrapper.innerHTML =
      '<button type="button" class="wishlist-btn wishlist-btn--card wishlist-btn--active"' +
          ' data-wishlist-handle="' + escapeHtml(product.handle) + '"' +
          ' data-add-label="Add to wishlist" data-remove-label="Remove from wishlist"' +
          ' aria-pressed="true" aria-label="Remove from wishlist">' +
        '<svg class="wishlist-icon wishlist-icon--empty" ' + svgAttrs + ' fill="none" stroke="currentColor"><path d="' + heartPath + '"/></svg>' +
        '<svg class="wishlist-icon wishlist-icon--filled" ' + svgAttrs + ' fill="currentColor" stroke="currentColor"><path d="' + heartPath + '"/></svg>' +
      '</button>' +
      '<div class="card card--standard card--media" style="--ratio-percent: 100%;">' +
        '<div class="card__inner color-background-1 gradient ratio" style="--ratio-percent: 100%;">' +
          '<div class="card__media">' +
            '<div class="media media--transparent media--hover-effect">' +
              '<a href="/products/' + escapeHtml(product.handle) + '" class="full-unstyled-link" aria-label="' + escapeHtml(product.title) + '">' +
                (imageUrl
                  ? '<img src="' + escapeHtml(imageUrl) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="600" height="600" class="motion-reduce">'
                  : '') +
              '</a>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="card__content">' +
          '<div class="card__information">' +
            '<div class="wishlist-title-row">' +
              '<div class="wishlist-title">' +
                '<h3 class="card__heading h5">' +
                  '<a href="/products/' + escapeHtml(product.handle) + '" class="full-unstyled-link">' +
                    escapeHtml(product.title) +
                  '</a>' +
                '</h3>' +
              '</div>' +
              '<div class="wishlist-quick-add">' +
                '<button type="button" class="wishlist-quick-add__button"' +
                  (available ? ' data-add-to-cart data-variant-id="' + (variant ? variant.id : '') + '"' : ' disabled') +
                  ' aria-label="' + escapeHtml(available ? addToCartLabel : soldOutLabel) + '">' +
                  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
                    '<path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
                  '</svg>' +
                '</button>' +
              '</div>' +
            '</div>' +
            buildPriceHtml(price, compareAtPrice) +
          '</div>' +
        '</div>' +
      '</div>';

    // Heart button on the wishlist page: clicking removes the item + the card from the DOM
    var heartBtn = wrapper.querySelector('.wishlist-btn--card');
    if (heartBtn) {
      heartBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        Wishlist.remove(product.handle);
        wrapper.remove();
        checkWishlistPageEmpty();
      });
    }

    // Add to cart
    var addBtn = wrapper.querySelector('[data-add-to-cart]');
    if (addBtn && available && variant) {
      addBtn.addEventListener('click', function () {
        addBtn.disabled = true;
        addBtn.classList.add('is-loading');
        addToCart({ id: variant.id, quantity: 1 })
          .then(function (ok) {
            addBtn.classList.remove('is-loading');
            if (ok) {
              addBtn.classList.add('is-added');
              setTimeout(function () {
                addBtn.classList.remove('is-added');
                addBtn.disabled = false;
              }, 1800);
            } else {
              addBtn.disabled = false;
            }
          })
          .catch(function () {
            addBtn.classList.remove('is-loading');
            addBtn.disabled = false;
          });
      });
    }

    return wrapper;
  }

  /** Show empty state if the wishlist page grid has no cards */
  function checkWishlistPageEmpty() {
    var grid = document.querySelector('.wishlist-page__grid');
    var empty = document.querySelector('.wishlist-empty');
    if (!grid) return;
    if (grid.children.length === 0) {
      grid.hidden = true;
      if (empty) empty.removeAttribute('hidden');
    }
  }

  /**
   * Render the wishlist page.
   * Only runs when .wishlist-page__grid is present in the DOM.
   */
  function renderWishlistPage() {
    var grid = document.querySelector('.wishlist-page__grid');
    var empty = document.querySelector('.wishlist-empty');
    if (!grid) return;

    var handles = Wishlist.get();

    if (handles.length === 0) {
      grid.hidden = true;
      if (empty) empty.removeAttribute('hidden');
      return;
    }

    if (empty) empty.setAttribute('hidden', '');
    grid.hidden = false;

    // Show skeleton loading state
    grid.innerHTML = '';
    for (var i = 0; i < handles.length; i++) {
      var skeleton = document.createElement('div');
      skeleton.className = 'wishlist-skeleton';
      grid.appendChild(skeleton);
    }

    // Fetch all products in parallel
    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })
    ).then(function (products) {
      grid.innerHTML = '';
      products.forEach(function (product, idx) {
        if (!product) {
          // Stale handle – clean up silently
          Wishlist.remove(handles[idx]);
          return;
        }
        grid.appendChild(createWishlistCard(product));
      });
      if (grid.children.length === 0) {
        grid.hidden = true;
        if (empty) empty.removeAttribute('hidden');
      }
    });
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  var wishlistDrawerCartItems = [];

  function getWishlistDrawer() {
    var root = document.querySelector('[data-wishlist-drawer-root]');
    if (!root) return null;
    return {
      root: root,
      drawer: root.querySelector('[data-wishlist-drawer]'),
      items: root.querySelector('[data-wishlist-drawer-items]'),
      count: root.querySelector('[data-wishlist-drawer-count]'),
      foot: root.querySelector('[data-wishlist-drawer-foot]'),
      addAllButton: root.querySelector('[data-wishlist-add-all]')
    };
  }

  function emptyDrawerHtml() {
    return (
      '<div class="apn__drawer-empty">' +
        '<svg width="42" height="42" viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="1.2" style="margin:0 auto;"><path d="M11 18.5S3 13.8 3 8.6C3 5.9 5.1 4 7.5 4c1.7 0 3 1 3.5 1.8C11.5 5 12.8 4 14.5 4 16.9 4 19 5.9 19 8.6c0 5.2-8 9.9-8 9.9Z"></path></svg>' +
        '<p>Your wishlist is empty</p>' +
        '<p>Tap the heart on any piece to save it here.</p>' +
      '</div>'
    );
  }

  function openWishlistDrawer() {
    var drawer = getWishlistDrawer();
    if (!drawer || !drawer.root || !drawer.drawer) return false;
    drawer.root.classList.add('is-drawer-open');
    drawer.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    renderWishlistDrawer();
    return true;
  }

  function closeWishlistDrawer() {
    var drawer = getWishlistDrawer();
    if (!drawer || !drawer.root || !drawer.drawer) return;
    drawer.root.classList.remove('is-drawer-open');
    drawer.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  function renderWishlistDrawer() {
    var drawer = getWishlistDrawer();
    if (!drawer || !drawer.items) return;

    var handles = Wishlist.get();
    wishlistDrawerCartItems = [];
    if (drawer.count) drawer.count.textContent = handles.length;
    if (drawer.foot) drawer.foot.hidden = true;
    if (drawer.addAllButton) {
      drawer.addAllButton.disabled = true;
      drawer.addAllButton.textContent = 'Add All to Cart';
    }

    if (!handles.length) {
      drawer.items.innerHTML = emptyDrawerHtml();
      return;
    }

    drawer.items.innerHTML = '<div class="apn__drawer-empty"><p>Loading wishlist...</p></div>';

    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + encodeURIComponent(handle) + '.js')
          .then(function (response) { return response.ok ? response.json() : null; })
          .catch(function () { return null; });
      })
    ).then(function (products) {
      var validProducts = products.filter(Boolean);

      if (!validProducts.length) {
        drawer.items.innerHTML = emptyDrawerHtml();
        return;
      }

      wishlistDrawerCartItems = validProducts
        .map(function (product) {
          var variant = (product.variants || []).find(function (item) { return item.available; });
          return variant ? { id: Number(variant.id), quantity: 1 } : null;
        })
        .filter(Boolean);

      if (drawer.foot) drawer.foot.hidden = !wishlistDrawerCartItems.length;
      if (drawer.addAllButton) drawer.addAllButton.disabled = !wishlistDrawerCartItems.length;

      drawer.items.innerHTML = validProducts.map(function (product) {
        var variant = (product.variants || []).find(function (item) { return item.available; }) || (product.variants || [])[0];
        var image = product.featured_image ? product.featured_image.replace(/(\.\w+)$/, '_120x120$1') : '';
        var price = variant ? variant.price : product.price;
        var compareAtPrice = variant ? variant.compare_at_price : product.compare_at_price;
        var available = variant && variant.available;

        return (
          '<div class="apn__drawer-item" data-wishlist-drawer-card="' + escapeHtml(product.handle) + '">' +
            '<a class="apn__drawer-swatch" href="/products/' + escapeHtml(product.handle) + '" aria-label="' + escapeHtml(product.title) + '">' +
              (image ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="120" height="120">' : '') +
            '</a>' +
            '<div style="flex:1;min-width:0;">' +
              '<a class="apn__drawer-name" href="/products/' + escapeHtml(product.handle) + '" style="text-decoration:none;">' + escapeHtml(product.title) + '</a>' +
              '<div class="apn__drawer-price">' + buildPriceHtml(price, compareAtPrice) + '</div>' +
              '<div class="apn__drawer-actions">' +
                '<button class="apn__drawer-cart" type="button" data-wishlist-drawer-cart="' + (variant ? variant.id : '') + '" ' + (available ? '' : 'disabled') +
                  ' aria-label="' + escapeHtml(available ? 'Add ' + product.title + ' to cart' : product.title + ' is sold out') + '">' +
                  CART_ICON_SVG +
                  '<span class="apn__drawer-cart-label">' + (available ? 'Add to cart' : 'Sold out') + '</span>' +
                '</button>' +
                '<button class="apn__drawer-remove" type="button" data-wishlist-drawer-remove="' + escapeHtml(product.handle) + '" aria-label="Remove ' + escapeHtml(product.title) + ' from wishlist">' +
                  REMOVE_ICON_SVG +
                '</button>' +
              '</div>' +
            '</div>' +
          '</div>'
        );
      }).join('');

      drawer.items.querySelectorAll('[data-wishlist-drawer-remove]').forEach(function (button) {
        button.addEventListener('click', function () {
          Wishlist.remove(button.dataset.wishlistDrawerRemove);
          updateAllButtons();
          updateHeaderCount();
          renderWishlistDrawer();
        });
      });

      drawer.items.querySelectorAll('[data-wishlist-drawer-cart]').forEach(function (button) {
        button.addEventListener('click', function () {
          var variantId = button.dataset.wishlistDrawerCart;
          if (!variantId) return;
          button.disabled = true;
          button.classList.add('is-loading');
          addToCart({ id: Number(variantId), quantity: 1 })
            .then(function (ok) {
              button.classList.remove('is-loading');
              if (ok) button.classList.add('is-added');
            })
            .catch(function () {
              button.classList.remove('is-loading');
            })
            .finally(function () {
              setTimeout(function () {
                button.disabled = false;
                button.classList.remove('is-added');
              }, 1400);
            });
        });
      });
    });
  }

  function initWishlistDrawer() {
    var drawer = getWishlistDrawer();
    if (!drawer || !drawer.root) return;

    document.querySelectorAll('.header__icon--wishlist, #wishlist-icon-bubble').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        openWishlistDrawer();
      });
    });

    drawer.root.querySelectorAll('[data-wishlist-drawer-close]').forEach(function (node) {
      node.addEventListener('click', closeWishlistDrawer);
    });

    if (drawer.addAllButton) {
      drawer.addAllButton.addEventListener('click', function () {
        if (!wishlistDrawerCartItems.length) return;
        drawer.addAllButton.disabled = true;
        drawer.addAllButton.textContent = 'Adding...';

        addToCart({ items: wishlistDrawerCartItems })
          .then(function (ok) {
            drawer.addAllButton.textContent = ok ? 'Added all' : 'Try again';
          })
          .catch(function () {
            drawer.addAllButton.textContent = 'Try again';
          })
          .finally(function () {
            setTimeout(function () {
              drawer.addAllButton.disabled = !wishlistDrawerCartItems.length;
              drawer.addAllButton.textContent = 'Add All to Cart';
            }, 1600);
          });
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.root.classList.contains('is-drawer-open')) {
        closeWishlistDrawer();
      }
    });

    window.WishlistDrawer = {
      open: openWishlistDrawer,
      close: closeWishlistDrawer,
      render: renderWishlistDrawer
    };

    renderWishlistDrawer();
  }

  function init() {
    updateAllButtons();
    updateHeaderCount();
    renderWishlistPage();
    initWishlistDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
